import { createServerFn } from "@tanstack/react-start";
import { getRequestHeaders } from "@tanstack/react-start/server";

import { apiFetch } from "./api";

export interface SessionInfo {
  staff: {
    groupId: string;
    groupName: string | null;
    id: string;
    permissions: string[];
  } | null;
  user: {
    email: string;
    emailVerified: boolean;
    id: string;
    name: string;
  } | null;
}

export const fetchSession = async (): Promise<SessionInfo> => {
  const data = await apiFetch<SessionInfo>("/api/me");
  return (
    data ?? {
      staff: null,
      user: null,
    }
  );
};

const unauthenticated: SessionInfo = { staff: null, user: null };

export const fetchSessionServer = createServerFn({ method: "GET" }).handler(
  async (): Promise<SessionInfo> => {
    const cookie = getRequestHeaders().get("cookie") ?? "";
    const { fetchFromApi } = await import("./api-server");

    try {
      const response = await fetchFromApi("/api/me", {
        headers: { cookie },
      });
      if (!response.ok) {
        return unauthenticated;
      }
      return (await response.json()) as SessionInfo;
    } catch {
      return unauthenticated;
    }
  }
);

export const can = (session: SessionInfo, permission: string): boolean =>
  session.staff?.permissions.includes(permission) ?? false;

const SESSION_REUSE_MS = 60_000;

let browserSession: { fetchedAt: number; session: SessionInfo } | null = null;

export const forgetSession = () => {
  browserSession = null;
};

export const rememberSession = (session: SessionInfo) => {
  if (session.staff && !browserSession) {
    browserSession = { fetchedAt: Date.now(), session };
  }
};

export const loadSession = async (): Promise<SessionInfo> => {
  if (import.meta.env.SSR) {
    return await fetchSessionServer();
  }
  if (
    browserSession &&
    Date.now() - browserSession.fetchedAt < SESSION_REUSE_MS
  ) {
    return browserSession.session;
  }
  const session = await fetchSession().catch(() => unauthenticated);
  browserSession = session.staff ? { fetchedAt: Date.now(), session } : null;
  return session;
};
